/**
 * Mail a report to one person, with the PDF attached.
 *
 *   POST {EMAIL_REGISTRY}/v1/email/add  Authorization: Bearer <emailRegistry token>
 *
 * Called straight from the browser, the same way the scheduler is - see
 * views/ScheduleReports/scheduleApi.js. A service token is minted first because the
 * registry will not take the app's own accessToken.
 *
 * ── THE FILE COMES FROM THE SCREEN, NOT FROM /generate-report ────────────────
 * Our backend can render a report (POST /generate-report, which is what the scheduler
 * calls), and it looked like the obvious source for the attachment. It is not, for this:
 * that endpoint reads a report's filters off the saved resource in Mongo, while the
 * filters a user applies on the dashboard are browser-only - Dashboard's
 * onApplyDataFilterToResource just calls setState and never writes back. A report
 * filtered down to twelve rows on screen would have been mailed with all two hundred.
 *
 * So the dialog attaches what the Download button would have produced, built by
 * buildRowsPdfAttachment from the rows actually on screen. /generate-report stays what it
 * was built for: the scheduled path, where there is no screen to match.
 *
 * ── ONE RECIPIENT ───────────────────────────────────────────────────────────
 * `To` and `ToName` are single strings in the registry's contract and the dialog collects
 * one of each. Sending to several would be several calls; nothing here assumes that is
 * impossible, but nothing offers it either.
 */

import { generateToken } from "../views/ScheduleReports/scheduleApi";

const { REACT_APP_EMAIL_REGISTRY_BASE_URL } = process.env;

/* The name the registry is registered under with the token service. Backend's
   instruction: "You can get the token from Token service (parameter: emailRegistry)". */
const EMAIL_SERVICE_NAME = "emailRegistry";

/* ── THE ROUTE ──
   Versioned, like every other Witmeg service here - /v1/scheduler/create,
   /v1/witmeg/otp/request. The spec first given to us said a bare /email/add, and the
   Endpoints gateway answered 404 "Method does not exist." with detail "service_control",
   which is Cloud Endpoints for "that path is not registered for this method" - it is
   refused at the gateway, before the token or the body is looked at. Corrected to /v1 on
   2026-09-28. */
const EMAIL_ROUTE = "/v1/email/add";

/* The token service distinguishes a login-server request from an ordinary service one.
   This is the latter: false. Passed explicitly rather than left to generateToken's
   default so the value is visible here, where the call is reasoned about. */
const IS_LOGIN_SERVER_REQUEST = false;

/* Fixed by the registry's contract - it uses these to pick the template and the sending
   identity, so they are not the caller's to vary. */
const CHANNEL = "Email";
const SERVICE_ORIGIN = "Witmeg Report";
const EMAIL_TYPE = "Standard";
const FROM_NAME = "Witmeg Report";
const FROM_ADDRESS = "no-reply@witmeg.com";

/* ── THE ATTACHMENT CEILING ──
   Stated as the real attachment size, which is what everyone downstream measures, and
   converted to an encoded length only because that is what we can count before sending.

   Checked here rather than left to the gateway because a rejected upload arrives as an
   opaque 413 with an HTML body - which reads on screen as "the email service is broken",
   and only after the user has waited for the whole thing to upload.

   ── WHERE 9 COMES FROM ──
   Backend, 2026-09-30: "It should be less than 10MB". That is the registry's own ceiling,
   and going over it is what produced POST /v1/email/add answering 200 with
   { Status: false, Result: "Unable to insert data in DB" } - a payload too large for the
   row it is stored in, reported as a generic insert failure.

   9 rather than 10 because the limit is on the REQUEST, and the attachments are not all
   of it: the subject, both message bodies and the addresses go in the same JSON. Those
   are a few KB, so a megabyte of headroom is far more than they need - but a send refused
   here names its size and tells the user what to do, while one refused there comes back
   as "Unable to insert data in DB" and means nothing to anybody.

   Comfortably under the two ceilings further downstream as well: SendGrid caps a message
   at about 30MB and Gmail refuses incoming over 25MB, so anything that passes this test
   should actually be delivered rather than bouncing silently later. */
export const MAX_ATTACHMENT_MB = 9;

// base64 carries 3 bytes in every 4 characters
export const MAX_ATTACHMENT_BASE64_CHARS = Math.ceil((MAX_ATTACHMENT_MB * 1024 * 1024) / 0.75);

/* Base64 is 4 characters per 3 bytes, so this recovers the real attachment size from the
   encoded length - which is what a mail provider's limit is actually about. */
const megabytesOf = (base64Chars) => (base64Chars * 0.75) / (1024 * 1024);

/* The size is named in the message. Without it "too large" is unarguable: a reader cannot
   tell whether they are barely over and should drop a column, or so far over that no
   amount of filtering will help - nor whether the limit is set sensibly. */
export const attachmentTooLarge = (base64Chars) =>
  `This report is ${megabytesOf(base64Chars).toFixed(1)}MB, over the ${MAX_ATTACHMENT_MB}MB limit for email. ` +
  `Try filtering it down, or download it and send it yourself.`;

/* Deliberately loose - the registry is the authority on what it will accept. This only
   catches the obvious typo before a token is minted and a few megabytes are uploaded. */
export const looksLikeEmail = (value) => typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/* The name, the message and the file name are typed by a user and land inside an HTML
   document. Unescaped, a name containing "<" breaks the markup, and a crafted one could
   put anything it liked into mail sent in our name. */
const escapeHtml = (value) =>
  String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const DEFAULT_MESSAGE = "Please find the attached report.";

/* A plain-text alternative is part of the contract and matters for delivery - a message
   with no text part scores worse with spam filters. Built from the same words as the HTML
   so the two cannot drift. */
/* "Attached:" for one file, "Attached (3):" for a dashboard's worth - so the reader can
   tell at a glance whether anything is missing from what they were sent. */
const attachedLabel = (fileNames) => (fileNames.length > 1 ? `Attached (${fileNames.length})` : "Attached");

/* ── UNSTYLED BODY (2026-10-15) ──
   Same words and order as before - greeting, message, "Attached:" with the file
   names, footer - as plain text: no card, border, background, colours or bold. The
   styled version is kept below (htmlBodyWrapped) for revert. */
// const plainTextBody = ({ message }) => message || DEFAULT_MESSAGE;
const plainTextBody = ({ toName, message, fileNames }) =>
  [
    `Hello ${toName || "there"},`,
    "",
    message || DEFAULT_MESSAGE,
    "",
    `${attachedLabel(fileNames)}:`,
    "",
    ...fileNames,
    "",
    "Sent from Witmeg Report.",
  ].join("\n");

// const htmlBody = ({ message }) =>
//   `<html><body><div>${escapeHtml(message || DEFAULT_MESSAGE).replace(/\r?\n/g, "<br />")}</div></body></html>`;
/* The same text in bare paragraphs - the mail client's default font and spacing,
   nothing set by us. Everything typed is escaped, and line breaks in the message
   are kept. */
const htmlBody = ({ toName, message, fileNames }) => `<html>
  <body>
    <p>Hello ${escapeHtml(toName || "there")},</p>
    <p>${escapeHtml(message || DEFAULT_MESSAGE).replace(/\r?\n/g, "<br />")}</p>
    <p>${attachedLabel(fileNames)}:</p>
    <p>${fileNames.map((name) => escapeHtml(name)).join("<br />")}</p>
    <p>Sent from Witmeg Report.</p>
  </body>
</html>`;

// eslint-disable-next-line no-unused-vars
const plainTextBodyWrapped = ({ toName, message, fileNames }) =>
  [
    `Hello ${toName || "there"},`,
    "",
    message || DEFAULT_MESSAGE,
    "",
    `${attachedLabel(fileNames)}:`,
    ...fileNames.map((name) => `  - ${name}`),
    "",
    "Sent from Witmeg Report.",
  ].join("\n");

/* Inline styles and a single column, nothing else. Mail clients strip stylesheets and
   disagree on everything past tables and inline rules, so this stays deliberately plain
   rather than matching the app's look and breaking in Outlook. */
// eslint-disable-next-line no-unused-vars
const htmlBodyWrapped = ({ toName, message, fileNames }) => `<html>
  <body style="margin:0;padding:24px;background:#f3f4f5;font-family:Arial,Helvetica,sans-serif;color:#333;">
    <div style="max-width:600px;margin:auto;background:#ffffff;border:1px solid rgba(0,0,0,0.1);border-radius:5px;padding:30px;">
      <p style="margin:0 0 16px;"><b>Hello ${escapeHtml(toName || "there")},</b></p>
      <p style="margin:0 0 16px;line-height:22px;">${escapeHtml(message || DEFAULT_MESSAGE)}</p>
      <p style="margin:0 0 6px;line-height:22px;color:#555;">${attachedLabel(fileNames)}:</p>
      <p style="margin:0 0 24px;line-height:22px;color:#555;">${fileNames
        .map((name) => `<b>${escapeHtml(name)}</b>`)
        .join("<br />")}</p>
      <p style="margin:0;padding-top:20px;border-top:1px solid #dfe1e2;color:#777;font-size:12px;">
        Sent from Witmeg Report.
      </p>
    </div>
  </body>
</html>`;

/* Same split as the scheduler's errors: the detail goes to the console, the user gets a
   sentence they can act on. A gateway's JSON or a fetch TypeError on screen is noise. */
function emailError(technical, friendly) {
  // eslint-disable-next-line no-console
  console.error("[email]", technical);
  const error = new Error(friendly || "The report could not be sent. Please try again.");
  error.technical = technical;
  return error;
}

/**
 * Send one message, with one or more reports attached, to one address.
 *
 * `attachments` is a list of what buildRowsPdfAttachment returns:
 * { fileName, fileType, fileContent }, with fileContent base64-encoded. A report has one;
 * a dashboard has one per table widget, which is why this takes a list rather than a
 * single file - the registry's Attachments field is an array, so a four-widget board is
 * one message with four PDFs rather than four messages.
 */
export async function sendReportEmail({ to, toName, subject, message, attachments, replyTo }) {
  if (!REACT_APP_EMAIL_REGISTRY_BASE_URL) {
    /* CRA inlines env vars at start-up, so a dev server started before this was added to
       .env sees it as undefined. Same trap as REACT_APP_TOKEN_SERVICE_URL - named here
       because the alternative is a fetch against "undefined/email/add". */
    throw emailError(
      "REACT_APP_EMAIL_REGISTRY_BASE_URL is not set. If it was just added to .env, restart the dev server — Create React App only reads env files at start-up.",
    );
  }

  const address = String(to || "").trim();
  if (!looksLikeEmail(address)) {
    throw emailError(`Refusing to send to "${address}"`, "Please enter a valid email address.");
  }
  const files = (Array.isArray(attachments) ? attachments : [attachments]).filter((file) => file && file.fileContent);
  if (!files.length) {
    throw emailError("sendReportEmail called with no attachment", "There is no data in this report to send.");
  }

  /* The limit is on the TOTAL, not on each file. A dashboard sends one message carrying
     every widget's PDF, and what a mail provider rejects is the size of the message - six
     files under the cap individually can still be well over it together. */
  const totalChars = files.reduce((sum, file) => sum + file.fileContent.length, 0);
  if (totalChars > MAX_ATTACHMENT_BASE64_CHARS) {
    /* Every file listed, not just the total: on a dashboard it is almost always ONE widget
       that is oversized, and this console line is what says which one. */
    const breakdown = files
      .map((file) => `${file.fileName} ${megabytesOf(file.fileContent.length).toFixed(2)}MB`)
      .join(", ");
    throw emailError(
      `${files.length} attachment(s) total ${megabytesOf(totalChars).toFixed(2)}MB, over the ${MAX_ATTACHMENT_MB}MB limit — ${breakdown}`,
      attachmentTooLarge(totalChars),
    );
  }

  const fileNames = files.map((file) => file.fileName);

  const token = await generateToken(EMAIL_SERVICE_NAME, IS_LOGIN_SERVER_REQUEST);

  const body = {
    Channel: CHANNEL,
    ServiceOrigin: SERVICE_ORIGIN,
    EmailType: EMAIL_TYPE,
    ToName: String(toName || "").trim() || address,
    To: address,
    FromName: FROM_NAME,
    From: FROM_ADDRESS,
    Subject: String(subject || "").trim() || fileNames[0],
    PlainTextContent: plainTextBody({ toName, message, fileNames }),
    HTMLContent: htmlBody({ toName, message, fileNames }),
    Attachments: files.map((file) => ({
      FileType: file.fileType || "pdf",
      FileContent: file.fileContent,
      FileName: file.fileName,
    })),
    /* Replies should reach whoever pressed Send, not the no-reply box the mail goes out
       from. Falls back to From when the account has no address on it. */
    Reply_To: String(replyTo || "").trim() || FROM_ADDRESS,
  };

  /* Logged on every send, not only on failure. The registry can refuse a message for
     reasons it describes only as "Unable to insert data in DB", and size is the first
     thing to correlate that against - without this line there is nothing to correlate it
     with, because a send that works logs nothing at all. */
  // eslint-disable-next-line no-console
  console.info(`[email] sending ${files.length} attachment(s), ${megabytesOf(totalChars).toFixed(2)}MB to ${address}`);

  let response;
  try {
    response = await fetch(`${REACT_APP_EMAIL_REGISTRY_BASE_URL}${EMAIL_ROUTE}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    /* fetch only throws for transport failures, and a CORS preflight the registry refuses
       is indistinguishable from the network being down: TypeError "Failed to fetch", no
       response to read. If this is what a first deployment hits, CORS for this origin is
       the thing to check. */
    throw emailError(`POST ${EMAIL_ROUTE} could not be reached: ${cause?.message || cause}`);
  }

  const text = await response.text();

  if (!response.ok) {
    /* 413 is worth its own message: it is the one failure the user can do something
       about, and the body will be the gateway's HTML rather than anything readable. */
    if (response.status === 413) {
      /* The gateway's own limit, hit despite passing ours - which means MAX_ATTACHMENT_
         BASE64_CHARS is set too high and should come down to whatever it actually is. */
      throw emailError(
        `POST ${EMAIL_ROUTE} rejected the payload as too large (413) at ${megabytesOf(totalChars).toFixed(2)}MB`,
        attachmentTooLarge(totalChars),
      );
    }
    throw emailError(`POST ${EMAIL_ROUTE} failed (${response.status}): ${text || "no response body"}`);
  }

  /* Parsed only to look for an envelope that reports failure inside a 200 - the pattern
     the scheduler uses, and worth guarding against here rather than reporting a silent
     non-delivery as success. A body that is not JSON is not itself a failure. */
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = null;
  }

  if (parsed && (parsed.Status === false || parsed.success === false)) {
    const reason = typeof parsed.Result === "string" ? parsed.Result : "";
    /* The size goes in the log line as well, because the registry's stock answer for a
       payload its database will not take is "Unable to insert data in DB" - which names
       no cause, and is the same string it would return for a schema problem. */
    throw emailError(
      `POST ${EMAIL_ROUTE} returned a failure envelope at ${megabytesOf(totalChars).toFixed(2)}MB: ${reason || text}`,
      reason || undefined,
    );
  }

  return parsed || { success: true };
}
